import { useEffect, useRef } from 'react';
import { useAppDispatch, useAppSelector } from '@core/hooks/redux-hooks';
import { InstrumentDefinitionWithId } from '@app/core/store/capital-structure-slice-selectors';
import {
  setExpandAllDiscountRateOverride,
  setExpandedDiscountRateOverrideInstruments,
  toggleDiscountRateOverrideInstrument,
} from '@core/store/ui-values-slice';

const useDiscountRateOverrideExpansion = (
  sortedValuedInstruments: InstrumentDefinitionWithId[]
) => {
  const dispatch = useAppDispatch();
  const expandedInstruments = useAppSelector(
    (state) => state.uiValues.expandedDiscountRateOverrideInstruments
  );
  const expandAll = useAppSelector((state) => state.uiValues.expandAllDiscountRateOverride);
  const isIndividualToggle = useRef(false);
  const prevExpandAll = useRef(expandAll);

  useEffect(() => {
    if (isIndividualToggle.current) {
      isIndividualToggle.current = false;
      prevExpandAll.current = expandAll;
      return;
    }
    if (expandAll === prevExpandAll.current) {
      return;
    }
    prevExpandAll.current = expandAll;
    if (expandAll) {
      const allExpanded: Record<string, boolean> = {};
      sortedValuedInstruments.forEach((instrument) => {
        allExpanded[instrument.instrumentId] = true;
      });
      dispatch(setExpandedDiscountRateOverrideInstruments(allExpanded));
    } else {
      dispatch(setExpandedDiscountRateOverrideInstruments({}));
    }
  }, [expandAll, sortedValuedInstruments, dispatch]);

  const toggleInstrumentExpanded = (instrumentId: string) => {
    dispatch(toggleDiscountRateOverrideInstrument(instrumentId));

    const newExpandedState = {
      ...expandedInstruments,
      [instrumentId]: !expandedInstruments[instrumentId],
    };
    const allExpanded = sortedValuedInstruments.every(
      (instrument) => newExpandedState[instrument.instrumentId]
    );
    const noneExpanded = sortedValuedInstruments.every(
      (instrument) => !newExpandedState[instrument.instrumentId]
    );

    if (allExpanded && !expandAll) {
      isIndividualToggle.current = true;
      dispatch(setExpandAllDiscountRateOverride(true));
    } else if ((noneExpanded || !allExpanded) && expandAll) {
      isIndividualToggle.current = true;
      dispatch(setExpandAllDiscountRateOverride(false));
    }
  };

  return { expandedInstruments, toggleInstrumentExpanded };
};

export default useDiscountRateOverrideExpansion;
